// Pure mission-planning logic for the map-based waypoint planner.
// No React, no MapLibre - everything here is unit-testable.
//
// Bounds mirror the backend waypointSchema in src/api/docks.ts exactly.
// Keep-in geofence semantics mirror src/services/dock-go-nogo.ts:
// a route passes when there are no active keep-in fences; otherwise every
// waypoint must fall inside at least one of them.

export type PlanWaypointAction = 'goto' | 'hover' | 'take_photo' | 'land';

export interface PlanWaypoint {
  lat: number;
  lng: number;
  /** Altitude AGL in metres. */
  alt: number;
  /** Cruise speed to this waypoint in m/s. */
  speed: number;
  action: PlanWaypointAction;
  hold_s: number;
}

export interface LatLng {
  lat: number;
  lng: number;
}

/** Active keep-in ('operational') geofence used for route validation. */
export interface KeepInFence {
  id: string;
  name: string;
  geometry: GeoJSON.Geometry;
}

export interface RouteViolation {
  /** Zero-based waypoint index the violation refers to. */
  index: number;
  severity: 'error' | 'warning';
  message: string;
}

export const PLAN_BOUNDS = {
  lat: [-90, 90],
  lng: [-180, 180],
  alt: [0, 1500],
  speed: [0, 30],
} as const;

export const DEFAULT_SPEED_MPS = 6;
export const DEFAULT_ALT_M = 50;
export const DEFAULT_HOLD_S = 5;
/** Distance from dock beyond which a waypoint is flagged as a warning. */
export const DOCK_RANGE_WARNING_M = 2000;

const EARTH_RADIUS_M = 6371000;

function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

/** Great-circle distance between two points in metres. */
export function haversineDistanceM(a: LatLng, b: LatLng): number {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const sinLat = Math.sin(dLat / 2);
  const sinLng = Math.sin(dLng / 2);
  const h = sinLat * sinLat + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * sinLng * sinLng;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Ray-casting point-in-polygon test. Ring is an array of [lng, lat] pairs
 * (GeoJSON order). Handles both closed (first === last) and open rings.
 * Port of the backend src/lib/pointInPolygon.ts so client validation matches
 * the server GO/NO-GO gate.
 */
export function pointInPolygonRing(lat: number, lng: number, ring: number[][]): boolean {
  if (ring.length < 3) return false;

  const open =
    ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1]
      ? ring.slice(0, -1)
      : ring;

  let inside = false;
  for (let i = 0, j = open.length - 1; i < open.length; j = i++) {
    const xi = open[i][1]; // lat
    const yi = open[i][0]; // lng
    const xj = open[j][1];
    const yj = open[j][0];

    const intersect =
      yi > lng !== yj > lng &&
      lat < ((xj - xi) * (lng - yi)) / (yj - yi) + xi;

    if (intersect) inside = !inside;
  }
  return inside;
}

/** Outer ring of a GeoJSON Polygon, or null for any other geometry. */
export function extractPolygonRing(geometry: GeoJSON.Geometry | null | undefined): number[][] | null {
  if (!geometry || geometry.type !== 'Polygon' || !Array.isArray(geometry.coordinates)) return null;
  return (geometry.coordinates[0] as number[][] | undefined) ?? null;
}

/** True when the point lies inside at least one fence polygon. */
export function isInsideAnyFence(lat: number, lng: number, fences: readonly KeepInFence[]): boolean {
  return fences.some((fence) => {
    const ring = extractPolygonRing(fence.geometry);
    return ring !== null && pointInPolygonRing(lat, lng, ring);
  });
}

export function createWaypoint(lat: number, lng: number, base?: Partial<PlanWaypoint>): PlanWaypoint {
  return {
    lat,
    lng,
    alt: DEFAULT_ALT_M,
    speed: DEFAULT_SPEED_MPS,
    action: 'goto',
    hold_s: DEFAULT_HOLD_S,
    ...base,
  };
}

/** Midpoint of two coordinates (planar average - fine at mission scale). */
export function midpoint(a: LatLng, b: LatLng): LatLng {
  return { lat: (a.lat + b.lat) / 2, lng: (a.lng + b.lng) / 2 };
}

/** Returns a new list with `wp` inserted at `index` (clamped to bounds). */
export function insertWaypointAt(
  list: readonly PlanWaypoint[],
  index: number,
  wp: PlanWaypoint,
): PlanWaypoint[] {
  const i = Math.max(0, Math.min(index, list.length));
  return [...list.slice(0, i), wp, ...list.slice(i)];
}

/** Returns a new list without the waypoint at `index`. */
export function removeWaypointAt(list: readonly PlanWaypoint[], index: number): PlanWaypoint[] {
  return list.filter((_, i) => i !== index);
}

/** Returns a new list with the waypoint at `index` swapped one step up/down. */
export function moveWaypoint(
  list: readonly PlanWaypoint[],
  index: number,
  dir: -1 | 1,
): PlanWaypoint[] {
  const j = index + dir;
  if (index < 0 || index >= list.length || j < 0 || j >= list.length) return [...list];
  const next = [...list];
  [next[index], next[j]] = [next[j], next[index]];
  return next;
}

/** Returns a new list with `patch` merged into the waypoint at `index`. */
export function updateWaypointAt(
  list: readonly PlanWaypoint[],
  index: number,
  patch: Partial<PlanWaypoint>,
): PlanWaypoint[] {
  return list.map((w, i) => (i === index ? { ...w, ...patch } : w));
}

/** Total route length in metres: home (when known) → wp1 → … → wpN. */
export function routeDistanceM(home: LatLng | null, waypoints: readonly PlanWaypoint[]): number {
  let total = 0;
  let prev: LatLng | null = home;
  for (const wp of waypoints) {
    if (prev) total += haversineDistanceM(prev, wp);
    prev = wp;
  }
  return total;
}

/**
 * Rough flight time in seconds. Each leg is flown at the destination
 * waypoint's speed (falling back to `defaultSpeed` when unset/invalid),
 * plus hover hold time at hover waypoints.
 */
export function estimateFlightTimeS(
  home: LatLng | null,
  waypoints: readonly PlanWaypoint[],
  defaultSpeed: number = DEFAULT_SPEED_MPS,
): number {
  let total = 0;
  let prev: LatLng | null = home;
  for (const wp of waypoints) {
    const speed = Number.isFinite(wp.speed) && wp.speed > 0 ? wp.speed : defaultSpeed;
    if (prev) total += haversineDistanceM(prev, wp) / speed;
    if (wp.action === 'hover' && Number.isFinite(wp.hold_s) && wp.hold_s > 0) total += wp.hold_s;
    prev = wp;
  }
  return total;
}

function inRange(v: number, [min, max]: readonly [number, number]): boolean {
  return Number.isFinite(v) && v >= min && v <= max;
}

export interface ValidateRouteOptions {
  /** Launch point (dock). Null when the dock has no coordinates. */
  home: LatLng | null;
  /** Active keep-in geofences. Empty array = geofence check passes. */
  keepInFences: readonly KeepInFence[];
}

/**
 * Live route validation. Errors block dispatch; warnings do not.
 * Rules mirror the backend waypointSchema bounds and the GO/NO-GO
 * keep-in geofence gate so the operator sees rejections before dispatch.
 */
export function validateRoute(
  waypoints: readonly PlanWaypoint[],
  options: ValidateRouteOptions,
): RouteViolation[] {
  const { home, keepInFences } = options;
  const violations: RouteViolation[] = [];

  for (const [i, wp] of waypoints.entries()) {
    const n = i + 1;
    if (!inRange(wp.lat, PLAN_BOUNDS.lat)) {
      violations.push({ index: i, severity: 'error', message: `WP${n}: LAT out of range (−90…90)` });
    }
    if (!inRange(wp.lng, PLAN_BOUNDS.lng)) {
      violations.push({ index: i, severity: 'error', message: `WP${n}: LNG out of range (−180…180)` });
    }
    if (!inRange(wp.alt, PLAN_BOUNDS.alt)) {
      violations.push({
        index: i,
        severity: 'error',
        message: `WP${n}: ALT ${Number.isFinite(wp.alt) ? Math.round(wp.alt) : '-'}m outside ${PLAN_BOUNDS.alt[0]}…${PLAN_BOUNDS.alt[1]}m`,
      });
    }
    if (!inRange(wp.speed, PLAN_BOUNDS.speed)) {
      violations.push({
        index: i,
        severity: 'error',
        message: `WP${n}: SPEED outside ${PLAN_BOUNDS.speed[0]}…${PLAN_BOUNDS.speed[1]} m/s`,
      });
    }
    if (wp.action === 'hover' && (!Number.isFinite(wp.hold_s) || wp.hold_s < 0)) {
      violations.push({ index: i, severity: 'error', message: `WP${n}: HOVER hold must be ≥ 0 s` });
    }

    const coordsValid = inRange(wp.lat, PLAN_BOUNDS.lat) && inRange(wp.lng, PLAN_BOUNDS.lng);
    if (coordsValid && keepInFences.length > 0 && !isInsideAnyFence(wp.lat, wp.lng, keepInFences)) {
      violations.push({
        index: i,
        severity: 'error',
        message: `WP${n}: outside all active keep-in geofences`,
      });
    }
    if (coordsValid && home) {
      const dist = haversineDistanceM(home, wp);
      if (dist > DOCK_RANGE_WARNING_M) {
        violations.push({
          index: i,
          severity: 'warning',
          message: `WP${n}: ${(dist / 1000).toFixed(1)} km from dock (>${(DOCK_RANGE_WARNING_M / 1000).toFixed(0)} km)`,
        });
      }
    }
  }

  return violations;
}

/** Indices of waypoints with at least one blocking (error) violation. */
export function errorIndices(violations: readonly RouteViolation[]): Set<number> {
  const out = new Set<number>();
  for (const v of violations) {
    if (v.severity === 'error') out.add(v.index);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Waypoint-action mapping (map planner ⇄ mission_waypoints store)
// ---------------------------------------------------------------------------
// The map planner speaks a narrow action vocabulary (goto|hover|take_photo|land)
// while the persisted mission_waypoints row uses the backend enum
// (fly_through|hover|take_photo|start_video|stop_video|land - see the
// waypointSchema in src/api/missions.ts). The only word that differs is
// goto ⇄ fly_through; everything the planner knows keeps its name. Actions the
// planner cannot represent (start_video/stop_video) collapse to 'goto' on load
// so a route round-trips without crashing the editor.

/** Action enum stored in mission_waypoints (backend waypointSchema). */
export type StoredWaypointAction =
  | 'fly_through'
  | 'hover'
  | 'take_photo'
  | 'start_video'
  | 'stop_video'
  | 'land';

/** Map planner action → persisted mission_waypoints action. */
export function planActionToStored(action: PlanWaypointAction): StoredWaypointAction {
  return action === 'goto' ? 'fly_through' : action;
}

/**
 * Persisted mission_waypoints action → map planner action.
 * fly_through → goto; hover/take_photo/land pass through; the video actions the
 * planner cannot express (start_video/stop_video) and any unknown value fall
 * back to 'goto' so a loaded route stays editable.
 */
export function storedActionToPlan(action: string | null | undefined): PlanWaypointAction {
  switch (action) {
    case 'hover':
    case 'take_photo':
    case 'land':
      return action;
    case 'fly_through':
    default:
      return 'goto';
  }
}

/** Persisted mission waypoint as returned by GET /api/missions/:id. */
export interface StoredWaypoint {
  latitude: number;
  longitude: number;
  altitude: number | null;
  speed?: number | null;
  action?: string | null;
  hover_duration_seconds?: number | null;
}

/** Payload row for PUT /api/missions/:id/waypoints. */
export interface WaypointPayload {
  sequence_order: number;
  latitude: number;
  longitude: number;
  altitude: number;
  speed: number;
  action: StoredWaypointAction;
  hover_duration_seconds?: number;
}

/**
 * Convert the current draft plan into the PUT /waypoints payload.
 * sequence_order is 1-based to match the backend (mission_waypoints stores
 * i + 1). hover_duration_seconds is only sent for hover waypoints, clamped to
 * the backend's [1, 300] range.
 */
export function planToWaypointPayload(waypoints: readonly PlanWaypoint[]): WaypointPayload[] {
  return waypoints.map((w, i) => {
    const row: WaypointPayload = {
      sequence_order: i + 1,
      latitude: w.lat,
      longitude: w.lng,
      altitude: w.alt,
      speed: w.speed,
      action: planActionToStored(w.action),
    };
    if (w.action === 'hover') {
      const hold = Math.round(w.hold_s);
      row.hover_duration_seconds = Math.min(300, Math.max(1, Number.isFinite(hold) ? hold : DEFAULT_HOLD_S));
    }
    return row;
  });
}

/** Rehydrate a persisted mission waypoint into a draft plan waypoint. */
export function storedToPlanWaypoint(stored: StoredWaypoint): PlanWaypoint {
  const alt = typeof stored.altitude === 'number' ? stored.altitude : DEFAULT_ALT_M;
  const speed = typeof stored.speed === 'number' && stored.speed > 0 ? stored.speed : DEFAULT_SPEED_MPS;
  const hold =
    typeof stored.hover_duration_seconds === 'number' && stored.hover_duration_seconds > 0
      ? stored.hover_duration_seconds
      : DEFAULT_HOLD_S;
  return createWaypoint(stored.latitude, stored.longitude, {
    alt,
    speed,
    action: storedActionToPlan(stored.action),
    hold_s: hold,
  });
}

/** Rehydrate an ordered list of persisted waypoints into a draft plan. */
export function storedWaypointsToPlan(stored: readonly StoredWaypoint[]): PlanWaypoint[] {
  return stored.map(storedToPlanWaypoint);
}

// ---------------------------------------------------------------------------
// Live mission progress (ArduPilot mission_current → operator waypoint)
// ---------------------------------------------------------------------------
// ArduPilot mission item numbering: item 0 = home, item 1 = takeoff, and
// operator waypoint K (0-based) is mission item K + 2. So the FC-reported
// mission_current maps back to an operator waypoint by subtracting 2.

/** Mission items that precede the first operator waypoint (home + takeoff). */
export const MISSION_ITEM_OFFSET = 2;

/**
 * Zero-based index of the operator waypoint the vehicle is currently flying to,
 * derived from the autopilot's mission_current. Returns null when there is no
 * usable value or the mission has no waypoints. Clamped to [0, count - 1] so a
 * mission_current that overshoots (e.g. RTL appended past the last waypoint)
 * pins to the final waypoint rather than pointing off the end.
 */
export function activeWaypointIndex(
  missionCurrent: number | null | undefined,
  waypointCount: number,
): number | null {
  if (missionCurrent === null || missionCurrent === undefined || !Number.isFinite(missionCurrent)) {
    return null;
  }
  if (waypointCount <= 0) return null;
  const raw = Math.floor(missionCurrent) - MISSION_ITEM_OFFSET;
  if (raw < 0) return 0;
  if (raw > waypointCount - 1) return waypointCount - 1;
  return raw;
}
